import { create } from 'zustand';
import { omit } from 'ramda';
import Config, { isAudi, isVolkswagen, BRAND_LETTER, isBentley, ENV } from '../../js/config/_global';
import api from '../../api';
import { sanitizeCollectionResponse, parseDealershipTitle } from '../../js/helpers';
import type { User, Dealership } from '../../api/types/index';
import type { CollectionResponse } from '../../api/types/response';
import { allCountries } from '../../js/countries';
import { ENV_TYPES, RESPONSE } from '../../js/_constants';

export type UserDealerships = {
    list: Dealership[];
    totalItems: number;
    currentPage: number;
    lastPage?: number;
    totalPages: number;
    error?: { code: number; message: string };
    raw?: CollectionResponse<Dealership> | null;
    dealershipGroups?: any[];
    dealerships?: Dealership[];
};

export interface UserState {
    loading: boolean;
    details: User & { isAllowedToUpload?: boolean; isWelcomeScreenEnabled?: boolean; isPinPairDisabled?: boolean; userGrantedBrands: string[] };
    dealerships: UserDealerships;
    isFirstLogin: boolean;
    dealershipGroups: any[];
    // Actions
    getUser: () => Promise<void>;
    getDealershipGroups: () => Promise<void>;
    setUser: (user: User & { dealerships: UserDealerships }) => void;
    resetUser: () => void;
}

const initialDetails: User = {
    '@id': '',
    '@type': '',
    dealershipGroups: [],
    sub: '',
    //@ts-ignore
    dealerships: [],
    affiliatedCountries: [],
    givenName: '',
    familyName: '',
    showTutorial: false,
    email: '',
    enabled: false,
    userGrantedBrands: [],
    isPinPairDisabled: false,
    isWelcomeScreenEnabled: false,
};

const initialState = {
    loading: false,
    details: initialDetails,
    dealershipGroups: [],
    dealerships: {
        list: [],
        totalItems: 0,
        currentPage: 1,
        totalPages: 1,
        raw: null
    },
    isFirstLogin: false,
};

const useUserStore = create<UserState>()((set, get) => ({
    ...initialState,

    getUser: async () => {
        set({ loading: true });

        try {
            const user = await api.getUser();
            const affiliatedCountries = allCountries.filter(country => 
                user.affiliatedCountries.includes(country[RESPONSE.ID])
            );
            
            const userMarkets = affiliatedCountries.map(country => country.iso3);

            user.affiliatedCountries = affiliatedCountries;
            user.isPinPairDisabled = (isAudi && userMarkets.some(market => 
                Config.pinPairDisabledCountries.includes(market)
            )) || isBentley;

            user.isWelcomeScreenEnabled = 
                ENV === ENV_TYPES.PROD 
                    ? (isBentley && userMarkets.some(market => 
                    Config.welcomeScreenEnabledCountries.includes(market)))
                    : (isBentley && userMarkets.some(market => 
                    Config.welcomeScreenEnabledCountriesExperimental.includes(market)));

            const dealerUploadRole = await api.user.getDealerUploadRule();

            // Brand Logic
            if (!isVolkswagen) {
                user.userGrantedBrands = [BRAND_LETTER];
            } else {
                user.userGrantedBrands = user.brands.reduce((acc: string[], brandData: string) => {
                    const brandLetter = brandData.split('/').pop();
                    if (brandLetter === 'V' || brandLetter === 'N') acc.push(brandLetter);
                    return acc;
                }, []);
            }

            let userDealerships: UserDealerships = { ...initialState.dealerships };

            try {
                const rawRes = await api.user.getUserDealerships({
                    params: { brand: user.userGrantedBrands }
                });
                const sanitized = sanitizeCollectionResponse<Dealership>(rawRes);
                sanitized.list = sanitized.list.map(dealer => ({
                    ...dealer,
                    title: parseDealershipTitle(dealer)
                }));

                userDealerships = { ...userDealerships, ...sanitized, raw: rawRes };
            } catch (error: any) {
                userDealerships.error = {
                    code: error?.code ?? 0,
                    message: 'Failed loading user dealerships.'
                };
            }

            set({
                loading: false,
                isFirstLogin: user.showTutorial,
                dealerships: userDealerships,
                details: {
                    ...omit(['channels', 'dealerships'], user),
                    isAllowedToUpload: affiliatedCountries.some(c => 
                        dealerUploadRole.countries?.includes(c.iso3)
                    ) && user.dealer,
                }
            });
        } catch (error) {
            set({ loading: false });
            throw error;
        }
    },

    getDealershipGroups: async () => {
        const groups = await api.user.getAllDealershipGroups(get().details.dealershipGroups);
        set({ dealershipGroups: groups });
    },

    setUser: (user) => set({
        loading: false,
        details: user,
        dealerships: user.dealerships
    }),

    resetUser: () => set(initialState),
}));

export default useUserStore;
